package com.hyperether.goodjob.scenes.user.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Card
import androidx.compose.material.Divider
import androidx.compose.material.Icon
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.navigation.NavHostController
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.Sp
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.navigation.*
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.delete
import com.hyperether.goodjob.resources.no_information
import com.hyperether.goodjob.resources.phone_1
import com.hyperether.goodjob.resources.selected
import com.hyperether.goodjob.resources.team
import com.hyperether.goodjob.scenes.components.CircleWithInitialUser
import com.hyperether.goodjob.scenes.components.RoundedCornerCheckbox
import com.hyperether.goodjob.theme.BorderColor
import com.hyperether.goodjob.util.UserStatusHelper
import com.hyperether.goodjob.util.UserStatusHelper.Companion.getStatusColorBasedOnConfirmStatus
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun UserList(
    users: List<User>,
    selectedUsers: MutableList<User>,
    isInEditMode: Boolean,
    navHostController: NavHostController
) {
    val checkedStates = remember { mutableStateMapOf<String, Boolean>() }
    LazyColumn(
        modifier = Modifier.fillMaxSize()
    ) {
        if (users.isNotEmpty()) {
            items(users.takeIf { it.isNotEmpty() } ?: emptyList()) { user ->
                UserItem(
                    user = user,
                    isInEditMode = isInEditMode,
                    isChecked = checkedStates[user._id] ?: false,
                    onUserSelected = { selectedUser, isSelected ->
                        checkedStates[user._id!!] = isSelected
                        if (isSelected) {
                            selectedUsers.add(selectedUser)
                        } else {
                            selectedUsers.remove(selectedUser)
                        }
                    },
                    onItemClick = {
                        if (getPlatformChecker().isMobile()) {
                            navHostController.navigate(UserDetails(user._id ?: ""))
                        } else {
                            navHostController.navigate(UserDetails(user._id ?: ""))
                        }
                    }
                )
                Divider(
                    //modifier = Modifier.padding(),
                    color = BorderColor,
                    thickness = Dp(0.5f)
                )
            }
        }
    }
    LaunchedEffect(selectedUsers.isEmpty()) {
        if (selectedUsers.isEmpty()) {
            checkedStates.clear()
        }
    }
}

@Composable
fun UserItem(
    isChecked: Boolean,
    user: User,
    isInEditMode: Boolean,
    onUserSelected: (User, Boolean) -> Unit,
    onItemClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(color = getStatusColorBasedOnConfirmStatus(user.confirmStatus))
            .padding(top = Dp(16f), bottom = Dp(16f), end = Dp(7f))
            .clickable {
                onItemClick()
            }
    ) {
        if (isInEditMode) {
            RoundedCornerCheckbox(
                modifier = Modifier.padding(start = Dp(16f)),
                isChecked = isChecked,
                onCheckedChange = { checked ->
                    onUserSelected(user, checked)
                }
            )
        }

        Spacer(modifier = Modifier.width(Dp(15f)))
//        Image(
//            painter = painterResource(Res.drawable.baseline_person_24),
//            contentDescription = null,
//            modifier = Modifier
//                .size(Dp(40f))
//                .clip(CircleShape)
//                .background(Color.LightGray.copy(alpha = 0.3f))
//                .align(Alignment.Top)
//        )
        CircleWithInitialUser(user, size = Dp(40f))

        Spacer(modifier = Modifier.width(Dp(8f)))

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = Dp(8f))
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = Dp(8f)),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = user.getName(),
                    fontSize = Sp(16f),
                    fontWeight = FontWeight.Bold
                )
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = Dp(4f)),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = UserStatusHelper.getStatusText(user.confirmStatus, user.status),
                    fontSize = Sp(12f),
                    color = Color.Gray
                )
            }

            val teams = user.teams?.mapNotNull { it?.value }
            if (teams?.isNotEmpty() == true) {
                Spacer(modifier = Modifier.height(Dp(4f)))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.team),
                        contentDescription = null,
                        modifier = Modifier.width(Dp(15f)).height(Dp(15f)),
                        tint = Color.Gray
                    )
                    Spacer(modifier = Modifier.width(Dp(8f)))
                    Text(
                        text = teams.joinToString(", ") { it },
                        fontSize = Sp(14f),
                        color = Color.DarkGray
                    )
                }
            }

            if (user.phone?.isNotEmpty() == true) {
                Spacer(modifier = Modifier.height(Dp(4f)))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.phone_1),
                        contentDescription = null,
                        modifier = Modifier.size(Dp(15f)),
                        tint = Color.Gray
                    )
                    Spacer(modifier = Modifier.width(Dp(8f)))
                    Text(
                        text = user.phone ?: stringResource(Res.string.no_information),
                        fontSize = Sp(14f),
                        color = Color.DarkGray
                    )
                }
            }
        }
    }
}

@Composable
fun EditDialog(
    showDialog: Boolean,
    employeesListSize: Int,
    onDelete: () -> Unit,
    onExportList: () -> Unit,
    onImportList: () -> Unit
) {
    if (showDialog) {
        Card(
            modifier = Modifier
                .width(Dp(300f))
                .clickable(
                    indication = null,
                    interactionSource = remember { MutableInteractionSource() },
                    onClick = { }
                )
                .padding(horizontal = Dp(16f), vertical = Dp(28f))
                .background(Color.White, shape = RoundedCornerShape(Dp(8f))),
            elevation = Dp(4f)
        ) {
            Column {
                Text(
                    text = "$employeesListSize " + stringResource(Res.string.selected),
                    fontWeight = FontWeight.W400,
                    color = Color.Gray,
                    fontSize = Sp(16f),
                    modifier = Modifier.padding(start = Dp(16f), end = Dp(16f), top = Dp(16f))
                )

                Spacer(modifier = Modifier.height(Dp(8f)))

                Divider(
                    color = Color.LightGray,
                    thickness = Dp(1f)
                )

                Spacer(modifier = Modifier.height(Dp(8f)))

                Row(
                    horizontalArrangement = Arrangement.SpaceBetween,
                    modifier = Modifier.fillMaxWidth()
                        .padding(start = Dp(16f), end = Dp(16f), bottom = Dp(16f))
                        .clickable {
                            onDelete()
                        }

                ) {
                    Text(
                        text = stringResource(Res.string.delete),
                        fontSize = Sp(14f),
                        color = Color.Red
                    )
//                    Text(
//                        text = stringResource(Res.string.export_list),
//                        fontSize = Sp(14f),
//                        color = BlueApp,
//                        modifier = Modifier.clickable {
//                            onExportList()
//                        })
//                    Text(
//                        text = stringResource(Res.string.import_list),
//                        fontSize = Sp(14f),
//                        color = BlueApp,
//                        modifier = Modifier.clickable {
//                            onImportList()
//                        })
                }
            }
        }
    }
}

